"use client";

import {
   useEffect,
   useId,
   useState,
   type FC,
   type FormEvent,
   type KeyboardEvent
} from "react";
import { Loader2, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { Avatar, AvatarFallback, Badge, Button, Input } from "@/app/components/ui";
import { api_url } from "@/app/api";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { KEY, METHOD_TYPE } from "@/app/lib/enums";
import {
   SHARE_INVITE_EMAIL_PATTERN,
   SHARE_INVITEE_LOOKUP_DEBOUNCE_MS
} from "@/app/lib/constants";
import type { ShareInvitee } from "@/types";

interface ShareInviteFormProps {
   ownerEmail: string;
   disabled: boolean;
   isInviting: boolean;
   onInvite: (emails: string[]) => Promise<boolean>;
}

export const ShareInviteForm: FC<ShareInviteFormProps> = ({
   ownerEmail,
   disabled,
   isInviting,
   onInvite
}) => {
   const translate = useTranslations("Home");
   const [text, setText] = useState("");
   const [chips, setChips] = useState<string[]>([]);
   const [invitee, setInvitee] = useState<ShareInvitee | null>(null);
   const inputId = useId();
   const ownEmail = ownerEmail.trim().toLowerCase();
   const isInvitable = (email: string) =>
      SHARE_INVITE_EMAIL_PATTERN.test(email) && email !== ownEmail;
   const typedEmail = text.trim().toLowerCase();
   const isInvitableEmail = isInvitable(typedEmail);
   const isFieldDisabled = disabled || isInviting;
   const inviteEmails = [
      ...new Set([...chips, ...(isInvitableEmail ? [typedEmail] : [])])
   ];

   useEffect(() => {
      setInvitee(null);
      if (!isInvitableEmail) return;

      const controller = new AbortController();
      const timer = setTimeout(async () => {
         try {
            const response = await fetchWithAuth(api_url.chat_history_share_invitee_url, {
               method: METHOD_TYPE.POST,
               body: JSON.stringify({ email: typedEmail }),
               signal: controller.signal
            });
            const { data } = response.ok ? await response.json() : { data: null };
            setInvitee(data ?? null);
         } catch {
            setInvitee(null);
         }
      }, SHARE_INVITEE_LOOKUP_DEBOUNCE_MS);

      return () => {
         clearTimeout(timer);
         controller.abort();
      };
   }, [isInvitableEmail, typedEmail]);

   const addChips = (emails: string[]) => {
      setChips((current) => {
         const next = [...current];
         emails.forEach((email) => {
            if (isInvitable(email) && !next.includes(email)) next.push(email);
         });
         return next;
      });
   };

   const handleTextChange = (value: string) => {
      if (!value.includes(",")) {
         setText(value);
         return;
      }

      const tokens = value.split(",").map((token) => token.trim());
      addChips(tokens.map((token) => token.toLowerCase()));
      setText(tokens.filter((token) => !isInvitable(token.toLowerCase())).join(" "));
   };

   const removeChip = (email: string) => {
      setChips((current) => current.filter((chip) => chip !== email));
   };

   const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      const isComma = event.key === ",";
      const isEnterWithText = event.key === KEY.Enter && Boolean(typedEmail);
      if (isComma || isEnterWithText) {
         event.preventDefault();
         if (!isInvitableEmail) return;

         addChips([typedEmail]);
         setText("");
         return;
      }

      const isEmptyBackspace = event.key === KEY.Backspace && !text && chips.length > 0;
      if (isEmptyBackspace) removeChip(chips[chips.length - 1]);
   };

   const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (inviteEmails.length === 0 || isFieldDisabled) return;

      const isInvited = await onInvite(inviteEmails);
      if (!isInvited) return;

      setChips([]);
      setText("");
   };

   return (
      <form onSubmit={handleSubmit} className="space-y-2">
         <div className="flex items-start gap-2">
            <label
               htmlFor={inputId}
               className="flex min-h-9 min-w-0 flex-1 cursor-text flex-wrap items-center gap-1 rounded-xl border border-input px-1.5 py-0.5 shadow-sm focus-within:ring-1 focus-within:ring-ring has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50"
            >
               {chips.map((chip) => (
                  <Badge
                     key={chip}
                     variant="outline"
                     className="max-w-full gap-0.5 rounded-lg bg-muted py-0 pl-2 pr-0.5 font-normal"
                  >
                     <span className="truncate">{chip}</span>
                     <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeChip(chip)}
                        disabled={isFieldDisabled}
                        aria-label={translate("removeShareInvite", { email: chip })}
                        className="h-5 w-5 shrink-0 rounded-md hover:bg-primary/10 hover:text-primary [&_svg]:size-3"
                     >
                        <X />
                     </Button>
                  </Badge>
               ))}
               <Input
                  id={inputId}
                  inputMode="email"
                  size="sm"
                  value={text}
                  onChange={(event) => handleTextChange(event.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                     chips.length ? "" : translate("shareInviteEmailPlaceholder")
                  }
                  aria-label={translate("shareInviteEmailPlaceholder")}
                  autoComplete="off"
                  disabled={isFieldDisabled}
                  className="h-7 min-w-[8rem] flex-1 border-0 px-1.5 text-sm shadow-none focus-visible:ring-0 disabled:opacity-100 md:text-sm"
               />
            </label>
            <Button
               type="submit"
               size="sm"
               disabled={isFieldDisabled || inviteEmails.length === 0}
               aria-busy={isInviting}
               className="relative h-9 shrink-0 px-4"
            >
               <span className={isInviting ? "invisible" : ""}>
                  {translate("shareInvite")}
               </span>
               {isInviting && <Loader2 className="absolute animate-spin" />}
            </Button>
         </div>
         {invitee && !isFieldDisabled && (
            <Button
               type="button"
               variant="outline"
               onClick={() => {
                  addChips([invitee.email]);
                  setText("");
               }}
               className="h-auto max-w-full justify-start gap-2 rounded-xl py-1 pl-1 pr-3 font-normal hover:border-primary/40 hover:bg-primary/10 hover:text-primary"
            >
               <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-xs font-semibold">
                     {invitee.email.charAt(0).toUpperCase()}
                  </AvatarFallback>
               </Avatar>
               <span className="min-w-0 truncate text-sm font-medium">
                  {invitee.email}
               </span>
            </Button>
         )}
      </form>
   );
};
